import type { RoutePreview } from '@stars-and-ships/api-types';
import { ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { formatDuration, formatNumber } from '../../labels';

export function RouteLine({ preview, name }: { preview: RoutePreview; name(id: string): string }) {
  const jumps = preview.legs.filter((l) => l.mode === 'gate').length;
  return (
    <div data-route-preview className="space-y-0.5">
      <p className="text-sm text-text">
        Arrives in{' '}
        <span className="font-mono tabular-nums text-info">
          {formatDuration(preview.totalS * 1000)}
        </span>{' '}
        · {jumps} warp gate jumps
      </p>
      <p className="text-xs text-muted">
        {formatNumber(preview.fleetSpeedLyph)} ly/h ·{' '}
        {preview.exitsNetworkAfterS > 0
          ? `leaves the gate network at ${name(preview.exitSystemId)} after ${formatDuration(preview.exitsNetworkAfterS * 1000)}`
          : 'direct travel from the start'}
      </p>
    </div>
  );
}

/**
 * What the player is about to send, and the button — at the end of the form, not pinned: pinned above
 * the phone nav it covered the ship rows while scrolling (Tim, 2026-09-18).
 */
export function DispatchSummary({
  route,
  cargo,
  shieldWarning,
  children,
}: {
  route: ReactNode;
  cargo: { text: string; over: boolean } | null;
  shieldWarning: boolean;
  children: ReactNode;
}) {
  return (
    <div data-dispatch-summary className="hud-cut space-y-2 border border-line-strong bg-panel p-3">
      {route}
      {cargo ? (
        <p
          data-cargo-capacity
          className={`font-mono text-xs tabular-nums ${cargo.over ? 'text-danger' : 'text-muted'}`}
        >
          {cargo.text}
        </p>
      ) : null}
      {shieldWarning ? (
        <p data-shield-warning className="flex items-start gap-2 text-sm text-accent">
          <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
          Attacking another commander lifts your beginner shield for good.
        </p>
      ) : null}
      {children}
    </div>
  );
}
